import { getApiClient, type GraphQLResult } from './apiClient';

export type VerifyMode = 'auto' | 'manual' | 'email';

export interface AuthConfig {
  verifyMode: VerifyMode;
  modes: VerifyMode[];
}

export interface PendingAccount {
  email: string;
  userType: string;
  createdAt: number;
}

export interface PendingList {
  accounts: PendingAccount[];
  total: number;
}

interface MutationResult {
  result: boolean;
  message: string;
  verifyMode?: VerifyMode;
}

const getAuthConfigQuery = /* GraphQL */ `
  query GetAuthConfig {
    getAuthConfig {
      verifyMode
      modes
    }
  }
`;

const setAuthConfigMutation = /* GraphQL */ `
  mutation SetAuthConfig($verifyMode: String!) {
    setAuthConfig(verifyMode: $verifyMode) {
      result
      message
      verifyMode
    }
  }
`;

const listPendingQuery = /* GraphQL */ `
  query ListPendingAccounts($page: Int, $pageSize: Int) {
    listPendingAccounts(page: $page, pageSize: $pageSize) {
      accounts {
        email
        userType
        createdAt
      }
      total
    }
  }
`;

const approveMutation = /* GraphQL */ `
  mutation ApproveAccount($email: String!) {
    approveAccount(email: $email) {
      result
      message
    }
  }
`;

const rejectMutation = /* GraphQL */ `
  mutation RejectAccount($email: String!) {
    rejectAccount(email: $email) {
      result
      message
    }
  }
`;

const unwrap = <T>(res: GraphQLResult<any>, key: string): T => {
  if (res.errors?.length) throw new Error(res.errors[0].message);
  return res.data?.[key] as T;
};

export const signupSettingsService = {
  async getConfig(): Promise<AuthConfig> {
    const res = await getApiClient().graphql({ query: getAuthConfigQuery });
    return unwrap<AuthConfig>(res, 'getAuthConfig');
  },

  async setMode(verifyMode: VerifyMode): Promise<MutationResult> {
    const res = await getApiClient().graphql({
      query: setAuthConfigMutation,
      variables: { verifyMode },
    });
    return unwrap<MutationResult>(res, 'setAuthConfig');
  },

  async listPending(page = 1, pageSize = 50): Promise<PendingList> {
    const res = await getApiClient().graphql({
      query: listPendingQuery,
      variables: { page, pageSize },
    });
    return unwrap<PendingList>(res, 'listPendingAccounts');
  },

  async approve(email: string): Promise<MutationResult> {
    const res = await getApiClient().graphql({ query: approveMutation, variables: { email } });
    return unwrap<MutationResult>(res, 'approveAccount');
  },

  async reject(email: string): Promise<MutationResult> {
    const res = await getApiClient().graphql({ query: rejectMutation, variables: { email } });
    return unwrap<MutationResult>(res, 'rejectAccount');
  },
};
